import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import styles from "./AddVisitRecordForm.module.css";
import TextField from "@mui/material/TextField";
import BasicDatePicker from "./Date-picker";
import dayjs from "dayjs";
import NumberInput from "./ui/NumberInput";
import Loader from "./Loader";
import { cleanEnglishOnly } from "../util/capitalize";

const getFormattedDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const AddVisitForm = ({
  onClose,
  pediatricianId,
  patientId,
  currentAge,
  ageInDays,
  gestationalAgeInDays,
  gestationalAgeInMonths,
  setsickVisitComplete,
  sickVisitComplete,
  visitData,
}) => {
  const [addVisitFormSectionsIndex, setAddVisitFormSectionsIndex] = useState(0);
  const initialstate = {
    visitDate: visitData?.visit_date_time
      ? visitData?.visit_date_time
      : getFormattedDateTime(),
    patientAge: visitData?.patient_age_in_months || currentAge,
    currentAgeInDays: visitData?.age_days || ageInDays,
    gestationalAgeInDays:
      (visitData && `${visitData?.gestational_age_in_days} Days`) ||
      gestationalAgeInDays,
    gestationalAgeInMonths:
      visitData?.gestational_age_in_months || gestationalAgeInMonths,
    reasonForVisit: visitData?.reason_for_visit || "",
    notes: visitData?.notes || "",
    diagnoses: visitData?.diagnoses || "",
    prescribedMedications: visitData?.prescribed_medications || "",
    recommendedTests: visitData?.recommended_tests || "",
    weight: visitData?.weight_kg || "",
    height: visitData?.height_cm || "",
    headCircumference: visitData?.head_circumference_cm || "",
    temperature: visitData?.temperature_c || "",
    heartRate: visitData?.heart_rate_bpm || "",
    respiratoryRate: visitData?.respiratory_rate_bpm || "",
    bloodPressure: visitData?.blood_pressure || "",
    developmentalMilestone: visitData?.developmental_status || "",
    feedingNutritionNotes: "",
    uploadDocuments: "",
    followUpDate: visitData?.follow_up_date
      ? dayjs(visitData?.follow_up_date)
      : null,
  };
  const [formData, setFormData] = useState(initialstate);

  const [fileName, setFilename] = useState("");
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("s2stoken");
  const navigate = useNavigate()
  const requiredFields = {
    0: ["reasonForVisit", "visitDate"],
    1: ["weight"],
    2: [],
  };
  const gestAge =
    typeof formData.gestationalAgeInDays === "string"
      ? formData.gestationalAgeInDays.split(" ")[0]
      : formData.gestationalAgeInDays;
  console.log(gestAge);
  console.log(visitData);
  const handleNext = () => {
    const currentRequiredFields = requiredFields[addVisitFormSectionsIndex];

    const isValid = currentRequiredFields.every(
      (field) => formData[field] && formData[field]?.trim() !== ""
    );

    if (!isValid) {
      toast.warn("Please fill in all required fields");
      return;
    }

    setAddVisitFormSectionsIndex((prev) => prev + 1);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "height" && isNaN(value)) {
      return toast.warn("Length must be a positive integer");
    }
    if (name === "headCircumference" && isNaN(value)) {
      return toast.warn("Head circumference must be a positive integer");
    }
    if (name === "heartRate" && isNaN(value)) {
      return toast.warn("Heart rate must be a positive integer");
    }
    if (name === "respiratoryRate" && isNaN(value)) {
      return toast.warn("Respiratory rate must be a positive integer");
    }
    if (name === "temperature" && isNaN(value)) {
      return toast.warn("Temperature must be a positive value");
    }
    if (name === "weight" && isNaN(value)) {
      return toast.warn("Weight must be a positive integer");
    }
    // If the input is a file input, we handle files instead of text values
    if (name === "uploadDocuments" && files && files[0]) {
      const file = files[0];
      setFilename(file.name);
      const reader = new FileReader();

      reader.onload = (event) => {
        const base64String = event.target.result.split(",")[1]; // Extract Base64 content
        setFormData((prevState) => ({
          ...prevState,
          [name]: base64String,
        }));
      };

      reader.readAsDataURL(file); // Convert file to Base64
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: cleanEnglishOnly(value) ?? "",
      }));
    }
  };
  console.log(formData);

  const handleVisitDateChange = (date) => {
    if (!date) {
      setFormData((prevState) => ({
        ...prevState,
        visitDate: "", // Set to empty string if date is cleared
      }));
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      visitDate: date.format("YYYY-MM-DD HH:mm"),
    }));
  };
  const handleDateChange = (date) => {
    console.log(date);

    // const dateObj = new Date(date.target.value);
    setFormData((prevState) => ({
      ...prevState,
      followUpDate: date,
      // followUpDate: dateObj.toLocaleDateString("en-CA"),
      //  Format as "YYYY-MM-DD",
    }));
  };
  console.log(formData.gestationalAgeInDays);
  console.log(gestationalAgeInDays);

  const getFormattedDate = (date) => {
    const visitDate = new Date(date);
    const formattedVisitDate = visitDate.toLocaleString("en-CA"); // 'YYYY-MM-DDTHH:mm:ss.sssZ'
    const formattedForBackend =
      formattedVisitDate.replace("T", " ").slice(0, 19) + "+0000";
    return formattedForBackend;
  };
  const getUpdateVisitFormattedDate = (date) => {
    if (!date) return null; // Handle undefined/null case

    const visitDate = new Date(date);
    return visitDate.toISOString().replace("T", " ").slice(0, 19) + " GMT";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);

    const payload = {};

    if (patientId) payload.patientId = patientId;
    if (pediatricianId) payload.pediatricianId = pediatricianId;
    if (formData.visitDate)
      payload.visitDateTime = getFormattedDate(
        formData.visitDate.toLocaleString("en-CA")
      );
    if (formData.reasonForVisit)
      payload.reasonForVisit = formData.reasonForVisit;
    if (formData.weight) payload.weight = formData.weight;
    if (formData.height) payload.length = formData.height;
    if (formData.patientAge) payload.currentAge = formData.patientAge;
    if (formData.currentAgeInDays)
      payload.currentAgeInDays = formData.currentAgeInDays;

    // Add other optional fields similarly
    if (formData.notes) payload.notes = formData.notes;
    if (formData.prescribedMedications)
      payload.prescribedMedications = formData.prescribedMedications;
    if (formData.temperature)
      payload.temperatureC = Number(formData.temperature);
    if (formData.heartRate) payload.heartRateBpm = formData.heartRate;
    if (formData.respiratoryRate)
      payload.respirationRateBpm = formData.respiratoryRate;
    if (formData.bloodPressure) payload.bloodPressure = formData.bloodPressure;
    if (formData.followUpDate)
      payload.followUpDate = getFormattedDate(formData.followUpDate);
    if (formData.diagnoses) payload.diagnoses = formData.diagnoses;
    if (formData.recommendedTests)
      payload.recommendedTests = formData.recommendedTests;
    if (formData.headCircumference)
      payload.headCircumference = formData.headCircumference;
    if (
      formData.gestationalAgeInDays !== "" &&
      formData.gestationalAgeInDays !== null
    ) {
      payload.gestationalAgeInDays = gestAge !== 0 ? gestAge : 0;
    }

    if (formData.gestationalAgeInMonths) {
      payload.gestationalAgeInMonths = formData.gestationalAgeInMonths;
    }
    // if (formData.uploadDocuments) {
    //   const reader = new FileReader();
    //   reader.onload = async function (event) {
    //     const binaryString = event.target.result;
    //     payload.uploadDocuments = btoa(binaryString);
    //   };
    // }

    payload.uploadDocuments = formData.uploadDocuments;

    const updateVisitRecordPayload = {
      patient_id: patientId,
      doctor_id: pediatricianId,
      visit_date_time: getUpdateVisitFormattedDate(formData.visitDate),
      reason_for_visit: formData.reasonForVisit,
      weight_kg: formData.weight,
      height_cm: formData.height,
      patient_age_in_months: formData.patientAge,
      age_days: formData.currentAgeInDays,
      notes: formData.notes,
      prescribed_medications: formData.prescribedMedications,
      temperature_c: formData.temperature,
      heart_rate_bpm: formData.heartRate,
      respiratory_rate_bpm: formData.respiratoryRate,
      blood_pressure: formData.bloodPressure,
      follow_up_date: formData.followUpDate
        ? getFormattedDate(formData.followUpDate)
        : null,
      diagnoses: formData.diagnoses,
      recommended_tests: formData.recommendedTests,
      head_circumference_cm: formData.headCircumference,
      gestational_age_in_days: gestAge,
      gestational_age_in_months: formData.gestationalAgeInMonths,
      upload_documents: formData.uploadDocuments,
    };

    console.log("Payload:", payload);
    console.log("updateVisitRecordPayload:", updateVisitRecordPayload);

    try {
      setLoading(true);
      let response;
      if (visitData !== null) {
        response = response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/update_visit_record/${visitData?.visit_id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            credentials: "include",
            body: JSON.stringify(updateVisitRecordPayload),
          }
        );
      } else {
        response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/add_visit_record_by_doctor`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            credentials: "include",
            body: JSON.stringify(payload),
          }
        );
      }

      const result = await response.json();
      console.log(result);

      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to add visit record");
      }

      if (response.ok) {
        setLoading(false);
        if (visitData !== null) {
          toast.success("Sick visit record updated successfully!");
        } else {
          toast.success("Sick visit record added successfully!");
        }
        setFilename("");
        setFormData(initialstate);
        setsickVisitComplete(!sickVisitComplete);
      }
    } catch (error) {
      setLoading(false);
      console.log("Error adding visit record:", error);
      setFilename("");
      setFormData(initialstate);
      if (error?.message === 'Token has expired') {
          localStorage.removeItem('s2stoken')
          navigate('/')
        }
    }

    onClose(); // Close the popup after form submission
  };

  const firstSection = (
    <div className={styles.grid}>
      <BasicDatePicker
        label={"Visit Date and Time"}
        value={dayjs(formData.visitDate)}
        className={styles.datetimeInput}
        onChange={handleVisitDateChange}
        required
      />
      <TextField
        type="text"
        name="patientAge"
        variant="outlined"
        size="small"
        label={
          formData.patientAge === 0
            ? "Actual Patient Age (in Days)"
            : "Actual Patient Age (in Months)"
        }
        value={
          formData.patientAge === 0
            ? formData.currentAgeInDays + " Days"
            : formData.patientAge
        }
        required
        slotProps={{
          input: {
            readOnly: true,
          },
        }}
        // min="1"
      />
      {currentAge <= 24 && (
        <TextField
          name="adjustedPatientAge"
          label={
            formData.gestationalAgeInMonths === 0
              ? "Adjusted Patient Age (in Days)"
              : "Adjusted Patient Age (in Months)"
          }
          value={
            Number(gestAge) !== 0
              ? formData.gestationalAgeInMonths === 0
                ? formData.gestationalAgeInDays
                : formData.gestationalAgeInMonths
              : "N/A"
          }
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          size="small"
        />
      )}
      <TextField
        type="text"
        name="reasonForVisit"
        variant="outlined"
        size="small"
        label="Reason For Visit"
        onChange={handleChange}
        value={formData.reasonForVisit}
        required
      />
      <TextField
        type="text"
        variant="outlined"
        size="small"
        label="Prescribed Medications"
        name="prescribedMedications"
        onChange={handleChange}
        value={formData.prescribedMedications}
      />
      <TextField
        type="text"
        name="diagnoses"
        variant="outlined"
        size="small"
        label="Diagnoses"
        onChange={handleChange}
        value={formData.diagnoses}
      />
      <TextField
        type="text"
        variant="outlined"
        size="small"
        label="Recommended Tests"
        name="recommendedTests"
        onChange={handleChange}
        value={formData.recommendedTests}
      />
    </div>
  );
  const middleSection = (
    <div className={styles.grid}>
      <NumberInput
        name="height"
        label="Length in cm (e.g. 50)"
        onChange={handleChange}
        value={formData.height}
        min="1"
      />
      <NumberInput
        name="weight"
        label="Weight in kg (e.g. 10)"
        onChange={handleChange}
        value={formData.weight}
        required
        step="0.1"
        min="0"
      />
      <NumberInput
        label="Head Circumference in cm"
        name="headCircumference"
        onChange={handleChange}
        value={formData?.headCircumference}
      />
      <NumberInput
        label="Heart Rate(bpm)"
        name="heartRate"
        onChange={handleChange}
        value={formData.heartRate}
      />
      <NumberInput
        label="Respiratory Rate(breaths/min.)"
        name="respiratoryRate"
        onChange={handleChange}
        value={formData.respiratoryRate}
      />
      <TextField
        type="text"
        variant="outlined"
        size="small"
        label="Blood Pressure"
        name="bloodPressure"
        onChange={handleChange}
        value={formData.bloodPressure}
      />
      <NumberInput
        label="Temperature(°F)"
        name="temperature"
        onChange={handleChange}
        value={formData.temperature}
        step="0.1"
      />
    </div>
  );

  const lastSection = (
    <div className={styles.lastGrid}>
      <div>
        <input
          type="file"
          id="uploadDocuments"
          name="uploadDocuments"
          onChange={handleChange}
          accept="application/pdf, image/*"
          style={{ display: "none" }}
        />
        <label
          htmlFor="uploadDocuments"
          style={{
            position: "relative",
            display: "block",
            padding: "8px 14px",
            border: "1px solid #c6c1c1",
            borderRadius: "4px",
            fontSize: "16px",
            color: "#000000",
            cursor: "pointer",
            backgroundColor: "#3071FF21",
            transition: "border-color 0.3s ease",
          }}
          onMouseEnter={(e) => {
            e.target.style.borderColor = "#1976d2";
          }}
          onMouseLeave={(e) => {
            e.target.style.borderColor = "#c6c1c1";
          }}
        >
          {fileName ? fileName : "Upload File"}{" "}
          <span
            style={{
              position: "absolute",
              right: "10px",
              top: "5px",
              fontSize: "20px",
            }}
          >
            📁
          </span>
        </label>
      </div>
      <div className="calender">
        <BasicDatePicker
          label="Next Appointment"
          onChange={handleDateChange}
          value={dayjs(formData.followUpDate)}
        />
        {formData.followUpDate && (
          <span
            className="clear-calender"
            onClick={() =>
              setFormData((prev) => ({ ...prev, followUpDate: null }))
            }
          >
            X
          </span>
        )}
      </div>
      <TextField
        type="text"
        name="notes"
        variant="outlined"
        size="small"
        label="Any additional comments"
        onChange={handleChange}
        value={formData.notes}
      />
    </div>
  );
  const AddVisitFormSection = [firstSection, middleSection, lastSection];

  return (
    <div className={styles.addVisitOverlay}>
      <div className={styles.formContainer}>
        <Loader isLoading={loading} />
        <div className={styles.formHeader}>
          <h2>Sick Visit Record</h2>
          <button id={styles.closeButton} onClick={onClose}>
            X
          </button>
        </div>
        <form className={styles.addVisitForm} onSubmit={handleSubmit}>
          {AddVisitFormSection[addVisitFormSectionsIndex]}
          <div className={styles.formButtons}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            {addVisitFormSectionsIndex !== 0 && (
              <button
                type="button"
                onClick={() => setAddVisitFormSectionsIndex((prev) => prev - 1)}
                className={styles.move}
              >
                {"<< Prev"}
              </button>
            )}

            {addVisitFormSectionsIndex !== 2 && (
              <button
                className={styles.move}
                type="button"
                onClick={handleNext}
              >
                {"Next >>"}
              </button>
            )}
            {addVisitFormSectionsIndex === 2 && (
              <button type="submit" className={styles.saveButton}>
                Submit
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVisitForm;
